import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ExternalLink, Sparkles, CheckCircle2, Gift, X, Trophy, Coins, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABLEFY_AFFILIATE_URL = "https://myablefy.com/s/erfolgbeginntindir/publisher-landing/XXEXTvL3LF_Wj2wy_QBw";

const ASSET = "https://customer-assets.emergentagent.com/job_quick-reels-53/artifacts";
const SHOTS = {
  s1a: `${ASSET}/oqi228im_Bildschirmfoto%202026-04-27%20um%2008.39.37.png`,
  s1b: `${ASSET}/tf3hjej9_Bildschirmfoto%202026-04-27%20um%2008.40.32.png`,
  s2a: `${ASSET}/am3vf6kq_Bildschirmfoto%202026-04-27%20um%2008.40.53.png`,
  s2b: `${ASSET}/0rld8zdh_Bildschirmfoto%202026-04-27%20um%2008.41.46.png`,
  s3a: `${ASSET}/vh8o18h1_Bildschirmfoto%202026-04-27%20um%2008.42.06.png`,
  s3b: `${ASSET}/56bvces3_Bildschirmfoto%202026-04-27%20um%2008.42.32.png`,
  s3c: `${ASSET}/ehgnrmq1_Bildschirmfoto%202026-04-27%20um%2008.42.50.png`,
  s4a: `${ASSET}/6aadckb4_Bildschirmfoto%202026-04-27%20um%2008.43.00.png`,
  s4b: `${ASSET}/hxd76hmr_Bildschirmfoto%202026-04-27%20um%2008.43.12.png`,
  s4c: `${ASSET}/1mun1ver_Bildschirmfoto%202026-04-27%20um%2008.43.39.png`,
  s5: `${ASSET}/gai5o0gj_Bildschirmfoto%202026-04-27%20um%2008.43.54.png`,
};

const STEPS = [
  {
    key: "intro",
    title: "Werde Premium Affiliate",
    subtitle: "Verdiene 45 % Provision auf jeden vermittelten Sale.",
    kind: "intro",
  },
  {
    key: "register",
    title: "Schritt 1 — Registriere dich bei Ablefy",
    subtitle: "Klicke auf den Button und lege in 60 Sekunden deinen Publisher-Account an.",
    images: [
      { src: SHOTS.s1a, caption: "Akzeptiere auf der Affiliate-Landingpage das Programm." },
      { src: SHOTS.s1b, caption: "Wenn du einen bestehenden Affiliate-Account hast, logge dich dort mit deinen Daten ein. Wenn du noch keinen hast, klicke einfach auf „Werde Affiliate\u201C." },
    ],
    note: "Nutze die selbe E-Mail wie bei Social IQ, damit wir dich später schneller zuordnen können.",
  },
  {
    key: "login",
    title: "Schritt 2 — Daten ausfüllen & ins Dashboard",
    subtitle: "Vervollständige dein Publisher-Profil und navigiere danach in die Übersicht.",
    images: [
      { src: SHOTS.s2a, caption: "Fülle alle deine Daten aus." },
      { src: SHOTS.s2b, caption: "Klicke danach auf der linken Seite auf „Übersicht\u201C, wenn du zum Dashboard geleitet wirst." },
    ],
  },
  {
    key: "program",
    title: "Schritt 3 — Affiliate-Link erstellen",
    subtitle: "Im Dashboard findest du den Link-Generator und wählst dort dein Programm.",
    images: [
      { src: SHOTS.s3a, caption: "Auf dem Dashboard findest du auf der rechten Seite den Button „Link erstellen\u201C." },
      { src: SHOTS.s3b, caption: "Als Nächstes wählst du das erste aus." },
      { src: SHOTS.s3c, caption: "Als Nächstes wählst du „Produkt\u201C aus." },
    ],
  },
  {
    key: "params",
    title: "Schritt 4 — Produkt & Bezahlseite auswählen",
    subtitle: "Wähle dein Produkt und die passende Bezahlseite. Den letzten Schritt kannst du überspringen.",
    images: [
      { src: SHOTS.s4a, caption: "Dann wählst du das Produkt aus und klickst es an." },
      { src: SHOTS.s4b, caption: "Anschließend wählst du die Bezahlseite aus." },
      { src: SHOTS.s4c, caption: "Diesen Schritt kannst du überspringen." },
    ],
  },
  {
    key: "share",
    title: "Herzlichen Glückwunsch — du hast es geschafft!",
    subtitle: "Dein Link ist generiert und du kannst direkt anfangen zu teilen und zu verdienen.",
    images: [
      { src: SHOTS.s5, caption: "Achte bitte darauf, dass du trotzdem deinen Account verifizierst, damit du dein Geld auch ausgezahlt bekommst. Das machst du unter „Einstellungen\u201C." },
    ],
    kind: "final",
  },
];

const TOTAL = STEPS.length; // includes intro

function ProgressDots({ current }) {
  return (
    <div className="flex items-center justify-center gap-1.5 mb-8" data-testid="affiliate-progress">
      {STEPS.map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i === current
              ? "w-8 bg-cyan-400"
              : i < current
                ? "w-1.5 bg-cyan-400/60"
                : "w-1.5 bg-zinc-700"
          }`}
        />
      ))}
    </div>
  );
}

function IntroPanel({ onStart, onSkip }) {
  return (
    <div className="text-center max-w-2xl mx-auto" data-testid="affiliate-intro-panel">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 mb-6">
        <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
        <span className="text-xs font-semibold tracking-wider uppercase text-cyan-300">Exklusiv für Premium-Mitglieder</span>
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Verdien <span style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>45 % Provision</span><br />
        mit Social IQ.
      </h1>
      <p className="text-base sm:text-lg text-zinc-400 mb-10 leading-relaxed">
        Du nutzt Social IQ bereits — empfiehl es weiter und verdiene <strong className="text-white">45 %</strong> auf jeden vermittelten <strong className="text-white">Lifetime-Kauf</strong>.
        Wir richten dich in 4 Schritten ein.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-10">
        <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 text-left" data-testid="benefit-card-1">
          <Coins className="w-5 h-5 text-cyan-400 mb-2" />
          <p className="text-sm font-semibold text-white mb-1">45 % pro Lifetime-Sale</p>
          <p className="text-xs text-zinc-500 leading-relaxed">Auf jeden vermittelten Lifetime-Kauf (97 €) deiner Empfehlung.</p>
        </div>
        <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 text-left" data-testid="benefit-card-2">
          <Trophy className="w-5 h-5 text-cyan-400 mb-2" />
          <p className="text-sm font-semibold text-white mb-1">Cookie-Tracking</p>
          <p className="text-xs text-zinc-500 leading-relaxed">Sales werden auch verzögert noch dir zugeordnet.</p>
        </div>
        <div className="p-4 rounded-2xl bg-zinc-900/60 border border-zinc-800 text-left" data-testid="benefit-card-3">
          <Share2 className="w-5 h-5 text-cyan-400 mb-2" />
          <p className="text-sm font-semibold text-white mb-1">UTM-Reports</p>
          <p className="text-xs text-zinc-500 leading-relaxed">Sieh genau, welcher Post welchen Sale gebracht hat.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={onStart}
          className="text-black font-semibold px-8 h-12 rounded-xl shadow-[0_0_24px_-6px_rgba(0,212,255,0.7)]"
          style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
          data-testid="affiliate-start-btn"
        >
          Jetzt einrichten <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <button
          onClick={onSkip}
          className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors px-4 py-2"
          data-testid="affiliate-skip-btn"
        >
          Vielleicht später
        </button>
      </div>

      <p className="text-xs text-zinc-600 mt-6">
        Du kannst jederzeit über das Menü <strong className="text-zinc-400">"Premium Affiliate"</strong> hierher zurückkehren.
      </p>
    </div>
  );
}

function StepPanel({ step, isFinal }) {
  return (
    <div className="max-w-3xl mx-auto" data-testid={`affiliate-step-${step.key}`}>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {step.title}
      </h2>
      <p className="text-sm sm:text-base text-zinc-400 text-center mb-6 max-w-xl mx-auto">
        {step.subtitle}
      </p>

      {step.note && (
        <div className="mb-6 p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-xs text-cyan-200/90 max-w-xl mx-auto text-center">
          {step.note}
        </div>
      )}

      {/* Visual screenshots — each with its own caption */}
      {step.images && step.images.length > 0 && (
        <div className={`grid gap-4 mb-6 ${step.images.length === 1 ? 'grid-cols-1 max-w-xl' : step.images.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} mx-auto`}>
          {step.images.map((img, i) => (
            <figure
              key={i}
              className="flex flex-col gap-3"
              data-testid={`affiliate-screenshot-${step.key}-${i}`}
            >
              <div className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-white shadow-xl">
                <img src={img.src} alt="" className="w-full h-auto block" loading="lazy" />
              </div>
              {img.caption && (
                <figcaption className="text-xs sm:text-sm text-zinc-300 leading-relaxed text-center px-1">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-[10px] font-semibold mr-1.5 align-middle">
                    {i + 1}
                  </span>
                  {img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}

      {/* CTA — open Ablefy in new tab */}
      <div className="flex justify-center mb-2">
        <a
          href={ABLEFY_AFFILIATE_URL}
          target="_blank"
          rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 hover:border-cyan-400/60 transition-all text-sm font-medium"
          data-testid={`ablefy-open-${step.key}`}
        >
          <ExternalLink className="w-4 h-4" />
          {isFinal ? "Affiliate-Link auf Ablefy holen" : "Auf Ablefy öffnen"}
        </a>
      </div>
    </div>
  );
}

export default function AffiliateInvitePage() {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useAuth();

  // Hard guard — only paying members
  useEffect(() => {
    if (!loading && user && user !== false && !user?.plan_active) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const dismiss = async () => {
    try {
      await axios.post(`${API}/me/affiliate/dismiss`, {}, { withCredentials: true });
      await refreshUser();
    } catch (_) { /* non-critical */ }
  };

  const finish = async () => {
    await dismiss();
    toast.success("Geschafft! Viel Erfolg als Premium Affiliate 🚀");
    navigate("/dashboard");
  };

  const skip = async () => {
    await dismiss();
    navigate("/dashboard");
  };

  const next = () => setIdx((i) => Math.min(i + 1, TOTAL - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  const step = STEPS[idx];
  const isIntro = step.kind === "intro";
  const isFinal = step.kind === "final";

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden" data-testid="affiliate-invite-page">
      {/* Background glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-cyan-500/10 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[300px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />

      {/* Close (top-right) */}
      <button
        onClick={skip}
        className="absolute top-4 right-4 z-30 p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition-colors"
        title="Schließen"
        data-testid="affiliate-close-btn"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <ProgressDots current={idx} />

        {/* Tag */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Gift className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Premium-Affiliate-Onboarding · {idx + 1} / {TOTAL}
          </span>
        </div>

        {/* Body */}
        {isIntro ? (
          <IntroPanel onStart={next} onSkip={skip} />
        ) : (
          <StepPanel step={step} isFinal={isFinal} />
        )}

        {/* Nav buttons (hide on intro — handled inside) */}
        {!isIntro && (
          <div className="flex items-center justify-between max-w-3xl mx-auto mt-10">
            <Button
              variant="ghost"
              onClick={prev}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800/60"
              data-testid="affiliate-prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
            </Button>

            {isFinal ? (
              <Button
                onClick={finish}
                className="text-black font-semibold px-6 h-11 rounded-xl shadow-[0_0_24px_-6px_rgba(0,212,255,0.7)]"
                style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
                data-testid="affiliate-finish-btn"
              >
                Fertig — zum Dashboard <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={next}
                className="text-black font-semibold px-6 h-11 rounded-xl shadow-[0_0_24px_-6px_rgba(0,212,255,0.7)]"
                style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
                data-testid="affiliate-next-btn"
              >
                Weiter <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
